import React from 'react';
import I18n from 'i18n-js';
import { RCIcon, useRCPopover } from '@reverbdotcom/cadence/components';
import {
  ArrowsClockwiseIcon,
  BellIcon,
  ChatCircleIcon,
  ChatsCircleIcon,
  ChevronRightIcon,
  NudgeBoxIcon,
  StorefrontIcon,
  TagIcon,
} from '@reverbdotcom/cadence/icons/react';
import { NotificationLink } from './notification_link';
import { NotificationsCenterQuery } from '@reverbdotcom/commons/src/gql/graphql';
import { SvgMapPin } from './action_icons';
import { clickedNotificationEvent } from './notification_event_props';

type NotificationTask = NotificationsCenterQuery['notificationTasks']['tasks'][number];

const MAX_COUNT = 99;

const TASK_ICONS: Record<string, JSX.Element> = {
  tag: <TagIcon />,
  'chat-circle': <ChatCircleIcon />,
  'arrows-clockwise': <ArrowsClockwiseIcon />,
  package: <NudgeBoxIcon />,
  'map-pin': <SvgMapPin />,
  storefront: <StorefrontIcon />,
  'chats-circle': <ChatsCircleIcon />,
};

function taskIcon(iconName: string): JSX.Element {
  return TASK_ICONS[iconName] ?? <BellIcon />;
}

interface IProps {
  tasks: NotificationTask[];
}

export default function TasksSection({ tasks }: IProps) {
  if (tasks.length === 0) return null;

  return (
    <ul className="notifications-center__tasks">
      {tasks.map(task => (
        <li key={task.category}>
          <TaskRow task={task} />
        </li>
      ))}
    </ul>
  );
}

export function TaskRow({ task }: { task: NotificationTask }) {
  // The panel is an RCPopover on desktop, which a click on its own content won't
  // dismiss. A no-op inside the mobile drawer, which the navigation closes anyway.
  const { close } = useRCPopover();

  return (
    <NotificationLink
      to={task.href}
      className="notifications-center__task"
      clickEvent={clickedNotificationEvent(task.eventTracking)}
      onClick={close}
    >
      <span className="notifications-center__task__icon">
        <RCIcon icon={taskIcon(task.iconName)} size={5} />
      </span>
      <span className="notifications-center__task__label">
        {task.title}
      </span>
      {task.unreadCount > 0 && (
        <span className="notifications-center__counter">
          {task.unreadCount > MAX_COUNT
            ? I18n.t('discovery.notificationsCenter.tasks.maxCount', { count: MAX_COUNT })
            : task.unreadCount}
        </span>
      )}
      <span className="notifications-center__task__chevron">
        <RCIcon icon={<ChevronRightIcon />} size={4} />
      </span>
    </NotificationLink>
  );
}
